import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, Download, Upload, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { PageHeader } from '@/components/ui/page-header'
import { UserForm } from '@/components/forms/UserForm'
import { ImportCsvModal } from '@/components/modals/ImportCsvModal'
import { apiClient } from '@/lib/api-client'
import { formatDate } from '@shared/utils'
import { toast } from 'react-hot-toast'

export function UsersPage() {
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const ITEMS_PER_PAGE = 12

  const handleExportCSV = async () => {
    try {
      const response = await apiClient.get('/users/export/csv', { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`
      link.click()
      window.URL.revokeObjectURL(url)
      toast.success('Users exported')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Export failed')
    }
  }

  const { data: users, isLoading } = useQuery({
    queryKey: ['users', searchQuery],
    queryFn: async () => {
      const response = await apiClient.get('/users')
      return response.data
    }
  })

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      await apiClient.delete(`/users/${userId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('User deleted successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete user')
    }
  })

  const handleEdit = (user: any) => {
    setSelectedUser(user)
    setIsFormOpen(true)
  }

  const handleDelete = async (userId: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteUserMutation.mutate(userId)
    }
  }

  const handleFormSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] })
    setIsFormOpen(false)
    setSelectedUser(null)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setSelectedUser(null)
  }

  const filteredUsers = users?.filter((user: any) => {
    if (!searchQuery) return true
    const searchLower = searchQuery.toLowerCase()
    return (
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      user.role.toLowerCase().includes(searchLower) ||
      (user.employeeId && user.employeeId.toLowerCase().includes(searchLower))
    )
  }) || []

  // Paginate filtered users
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex)

  // Reset pagination when search changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Users"
        actions={
          <>
            <Button variant="outline" onClick={() => setIsImportOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Import
            </Button>
            <Button variant="outline" onClick={handleExportCSV}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            <Button onClick={() => setIsFormOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add user
            </Button>
          </>
        }
      />

      <div className="data-panel">
        <div className="border-b border-border px-3 py-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search name, email, or ID"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="h-11 pl-9 lg:h-9"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-muted-foreground">
            No users found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Employee ID</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((person: any) => (
                  <tr key={person.id}>
                    <td>
                      <Link to={`/users/${person.id}`} className="font-medium text-primary hover:underline">
                        {person.name}
                      </Link>
                    </td>
                    <td data-label="Email">{person.email}</td>
                    <td data-label="Employee ID" className="font-mono text-xs">{person.employeeId || '—'}</td>
                    <td data-label="Role">
                      <Badge variant={person.role === 'Admin' ? 'secondary' : 'outline'}>
                        {person.role}
                      </Badge>
                    </td>
                    <td data-label="Joined" className="text-muted-foreground">{formatDate(person.createdAt)}</td>
                    <td className="is-actions">
                      <div className="flex justify-end gap-1">
                        <Button asChild variant="ghost" size="icon" className="h-8 w-8" aria-label="View user">
                          <Link to={`/users/${person.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(person)} aria-label="Edit user">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(person.id)}
                          disabled={deleteUserMutation.isPending}
                          aria-label="Delete user"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border px-3 py-2 text-sm text-muted-foreground">
          <span>
            {filteredUsers.length === 0
              ? '0 users'
              : `${startIndex + 1}–${Math.min(endIndex, filteredUsers.length)} of ${filteredUsers.length}`}
          </span>
          {totalPages > 1 && (
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="h-8" disabled={currentPage === 1} onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-8" disabled={currentPage === totalPages} onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <UserForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        user={selectedUser}
        onSuccess={handleFormSuccess}
      />
      <ImportCsvModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        title="Import users"
        templateUrl="/users/import/template"
        importUrl="/users/import/csv"
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['users'] })}
      />
    </div>
  )
}
